export const MATCH_THRESHOLD = 0.75;
const MIN_WORD_SCORE = 0.6;
const MIN_TYPO_SCORE = 0.7;

const SEARCH_FIELDS = [
  { key: "no", label: "No.", weight: 1 },
  { key: "description", label: "Description", weight: 1 },
  { key: "itemCategory", label: "Item Category", weight: 0.9 },
  { key: "baseUOM", label: "Unit", weight: 0.9 },
];

const hasDigit = (text) => /\d/.test(text);

export const normalizeText = (value) =>
  String(value ?? "")
    .toUpperCase()
    .replace(/[^A-Z0-9%.]+/g, " ")
    .replace(/(^|\s)\.+|\.+(?=\s|$)/g, " ")
    .trim();

export const splitWords = (value) => normalizeText(value).split(" ").filter(Boolean);

const editDistance = (a, b) => {
  const row = Array.from({ length: b.length + 1 }, (_, j) => j);
  for (let i = 1; i <= a.length; i += 1) {
    let diagonal = row[0];
    row[0] = i;
    for (let j = 1; j <= b.length; j += 1) {
      const above = row[j];
      row[j] = Math.min(
        row[j] + 1,
        row[j - 1] + 1,
        diagonal + (a[i - 1] === b[j - 1] ? 0 : 1),
      );
      diagonal = above;
    }
  }
  return row[b.length];
};

const similarity = (a, b) => 1 - editDistance(a, b) / Math.max(a.length, b.length);

const scoreWord = (query, word) => {
  if (word === query) return { score: 1, kind: "exact" };

  const numeric = hasDigit(query);

  if (word.startsWith(query)) {
    const next = word[query.length];
    if (!numeric || !/[\d.]/.test(next)) {
      return { score: query.length >= 2 ? 0.97 : 0.9, kind: "starts" };
    }
  }

  if (query.length >= 3) {
    const at = word.indexOf(query);
    if (at > 0 && (!numeric || !/\d/.test(word[at - 1]))) {
      return { score: numeric ? 0.8 : 0.85, kind: "contains" };
    }
  }

  if (numeric || query.length < 4) return { score: 0, kind: "none" };
  if (word.length < query.length - 3) return { score: 0, kind: "none" };

  let score = similarity(query, word);
  if (word.length > query.length) {
    score = Math.max(score, similarity(query, word.slice(0, query.length)) * 0.95);
  }
  if (query[0] !== word[0]) score *= 0.85;

  return score >= MIN_TYPO_SCORE ? { score, kind: "similar" } : { score: 0, kind: "none" };
};

const preparedCache = new WeakMap();

const prepareItem = (item) => {
  let prepared = preparedCache.get(item);
  if (!prepared) {
    prepared = SEARCH_FIELDS.map((field) => {
      const sequence = splitWords(item[field.key]);
      return { ...field, sequence, words: [...new Set(sequence)] };
    });
    preparedCache.set(item, prepared);
  }
  return prepared;
};

const JOINED_SCORE = 0.95;
const MIN_JOINED_LENGTH = 4;

// Matches a run-together query such as "SOAPBAR" or "SOAPBL" against the words
// of a field in order: every piece must be a whole word except the last, which
// may be a prefix. Words in between may be skipped.
const joinedMatch = (query, sequence) => {
  const walk = (pos, from) => {
    for (let i = from; i < sequence.length; i += 1) {
      const word = sequence[i];
      const rest = query.slice(pos);
      if (word.startsWith(rest)) return [word];
      if (rest.startsWith(word)) {
        const tail = walk(pos + word.length, i + 1);
        if (tail) return [word, ...tail];
      }
    }
    return null;
  };

  for (let start = 0; start < sequence.length; start += 1) {
    if (!query.startsWith(sequence[start]) || query === sequence[start]) continue;
    const tail = walk(sequence[start].length, start + 1);
    if (tail) return [sequence[start], ...tail];
  }
  return null;
};

const bestForQueryWord = (queryWord, fields, wordCache) => {
  let best = { score: 0, kind: "none", field: "", word: "", query: queryWord };
  for (const field of fields) {
    for (const word of field.words) {
      let result = wordCache?.get(word);
      if (!result) {
        result = scoreWord(queryWord, word);
        wordCache?.set(word, result);
      }
      const score = result.score * field.weight;
      if (score > best.score) {
        best = { score, kind: result.kind, field: field.label, word, query: queryWord };
      }
    }
  }

  if (best.score < JOINED_SCORE && queryWord.length >= MIN_JOINED_LENGTH) {
    for (const field of fields) {
      const score = JOINED_SCORE * field.weight;
      if (score <= best.score) continue;
      const words = joinedMatch(queryWord, field.sequence);
      if (words) {
        best = {
          score,
          kind: "joined",
          field: field.label,
          word: words.join(" "),
          words,
          query: queryWord,
        };
      }
    }
  }

  return best;
};

export const scoreItem = (item, query, wordCaches) => {
  const queryWords = splitWords(query);
  if (!queryWords.length) return null;

  const compactQuery = queryWords.join("");
  const itemNo = normalizeText(item.no).replace(/\s+/g, "");

  if (itemNo === compactQuery) {
    return {
      score: 1,
      tier: 0,
      details: [{ score: 1, kind: "exact", field: "No.", word: item.no, query }],
    };
  }

  if (compactQuery.length >= 2 && itemNo.startsWith(compactQuery)) {
    return {
      score: 0.99,
      tier: 1,
      details: [{ score: 0.99, kind: "starts", field: "No.", word: item.no, query }],
    };
  }

  const fields = prepareItem(item);
  const details = queryWords.map((word) =>
    bestForQueryWord(word, fields, wordCaches?.get(word)),
  );

  if (details.some((detail) => detail.score < MIN_WORD_SCORE)) return null;

  const score = details.reduce((sum, detail) => sum + detail.score, 0) / details.length;
  if (score < MATCH_THRESHOLD) return null;

  const allDirect = details.every(
    (detail) => detail.kind === "exact" || detail.kind === "starts" || detail.kind === "joined",
  );

  return { score, tier: allDirect ? 2 : 3, details };
};

export const searchItems = (items, query) => {
  const queryWords = splitWords(query);
  if (!queryWords.length) return [];

  const wordCaches = new Map(queryWords.map((word) => [word, new Map()]));

  return items
    .map((item) => {
      const match = scoreItem(item, query, wordCaches);
      return match ? { ...item, match } : null;
    })
    .filter(Boolean)
    .sort(
      (a, b) =>
        a.match.tier - b.match.tier ||
        b.match.score - a.match.score ||
        String(a.description || "").length - String(b.description || "").length ||
        String(a.no || "").localeCompare(String(b.no || "")),
    );
};

const describeDetail = (detail) => {
  if (detail.kind === "similar") return `${detail.word} (close to ${detail.query})`;
  if (detail.kind === "contains") return `${detail.word} (contains ${detail.query})`;
  return detail.word;
};

export const describeMatch = (match) => {
  if (!match) return "";
  const byField = new Map();
  match.details.forEach((detail) => {
    const list = byField.get(detail.field) || [];
    list.push(describeDetail(detail));
    byField.set(detail.field, list);
  });
  const parts = [...byField.entries()].map(
    ([field, words]) => `${field}: ${[...new Set(words)].join(", ")}`,
  );
  return parts.join(" · ");
};

export const matchedWordsFor = (match, fieldLabel) =>
  match
    ? match.details
        .filter((detail) => detail.field === fieldLabel && detail.word)
        .flatMap((detail) => detail.words || [detail.word])
    : [];
